// ---------------------------------------------------------------------------
// Shared formatting utilities for reimagined pages
// ---------------------------------------------------------------------------

/**
 * Format a number as US currency string (e.g., "$42,000").
 *
 * Uses `Intl.NumberFormat` with zero fraction digits by default.
 * Pass `fractionDigits` to include cents.
 */
export function formatCurrency(amount: number | undefined | null, fractionDigits = 0): string {
  if (amount == null || Number.isNaN(amount)) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

/**
 * Format a date as "Jan 2025" (short month + year).
 */
export function formatMonthYear(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(d);
}

/**
 * Format a date as "Jan 15, 2025" (short month + day + year).
 */
export function formatShortDate(date: Date | string | undefined | null): string {
  if (!date) return '';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as "1/15/2025" (numeric month/day/year).
 */
export function formatNumericDate(date: Date | string | undefined | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "Jan 2025" (short month + year).
 *
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2026-01")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const date = new Date(Number(dashParts[0]), Number(dashParts[1]) - 1);
    if (!isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2026M01")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const date = new Date(Number(periodMatch[1]), Number(periodMatch[2]) - 1);
    if (!isNaN(date.getTime())) {
      return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(fallback);
}

/**
 * Format a benefit cycle string ("YYYY-MM") as "December 2025 Benefit Cycle".
 *
 * Uses the full month name followed by the year and " Benefit Cycle" suffix.
 * Falls back to standard date parsing for non-cycle strings.
 */
export function formatCycleDateLong(dateStr: string | undefined | null): string {
  if (!dateStr) return '—';

  // Handle "YYYY-MM" format (e.g., "2025-12")
  const dashParts = dateStr.split('-');
  if (dashParts.length >= 2) {
    const monthNum = Number(dashParts[1]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(dashParts[0]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        const monthYear = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
        }).format(date);
        return `${monthYear} Benefit Cycle`;
      }
    }
  }

  // Handle accounting period "YYYYMmm" format (e.g., "2025M12")
  const periodMatch = dateStr.match(/^(\d{4})M(\d{2})$/);
  if (periodMatch) {
    const monthNum = Number(periodMatch[2]);
    if (monthNum >= 1 && monthNum <= 12) {
      const date = new Date(Number(periodMatch[1]), monthNum - 1);
      if (!isNaN(date.getTime())) {
        const monthYear = new Intl.DateTimeFormat('en-US', {
          month: 'long',
          year: 'numeric',
        }).format(date);
        return `${monthYear} Benefit Cycle`;
      }
    }
  }

  // Fallback: try standard date parsing
  const fallback = new Date(dateStr);
  if (isNaN(fallback.getTime())) return '—';
  const monthYear = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
    fallback
  );
  return `${monthYear} Benefit Cycle`;
}
